import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import { Alert, Box, Button, LinearProgress, Paper, Skeleton, Stack, Typography } from '@mui/material';
import type { UseQueryResult } from '@tanstack/react-query';
import { useNavigate } from 'react-router';

import type { LearningCourseSummary } from '@drivingdoc/api-client/generated/types';

interface Props {
  courses: UseQueryResult<LearningCourseSummary[], Error>;
  courseIds: string[];
}

export function formatCourseTime(seconds: number): string {
  const safeSeconds = Math.max(0, seconds);
  const hours = Math.floor(safeSeconds / 3600);
  const minutes = Math.floor((safeSeconds % 3600) / 60);
  if (hours === 0) return `${minutes} min`;
  if (minutes === 0) return `${hours} hr`;
  return `${hours} hr ${minutes} min`;
}

export default function OnlineCourseProgressCard({ courses, courseIds }: Props) {
  const navigate = useNavigate();
  const courseIdSet = new Set(courseIds);
  const availableCourses = courses.data?.filter((course) => courseIdSet.has(course.courseId)) ?? [];

  if (courses.isPending && courseIds.length > 0) {
    return <Skeleton variant="rounded" height={180} aria-label="Loading online course progress" />;
  }

  if (courses.isError && courseIds.length > 0) {
    return <Alert severity="error">Online course progress could not be loaded. Please refresh and try again.</Alert>;
  }

  if (availableCourses.length === 0) return null;

  return (
    <Stack spacing={2} sx={{ mb: 4 }} data-testid="online-course-progress">
      <Typography variant="h6" fontWeight={700}>
        Online Course Progress
      </Typography>
      {availableCourses.map((course) => {
        const quizAverage = course.passedQuizCount
          ? `${Math.round(course.passedQuizScoreSum / course.passedQuizCount)}%`
          : 'No completed quizzes';
        const timeLabel = course.requiredSeconds
          ? `${formatCourseTime(course.secondsSpent)} recorded / ${formatCourseTime(course.requiredSeconds)} required`
          : `${formatCourseTime(course.secondsSpent)} recorded`;

        return (
          <Paper key={course.courseId} variant="outlined" sx={{ p: { xs: 2, sm: 3 } }}>
            <Stack spacing={2}>
              <Stack direction={{ xs: 'column', sm: 'row' }} justifyContent="space-between" gap={2}>
                <Box>
                  <Typography variant="h6">{course.title}</Typography>
                  <Typography color="text.secondary">
                    {course.completedAt ? 'Course complete' : `${course.progressPercent}% complete`}
                  </Typography>
                </Box>
                <Button
                  variant="contained"
                  endIcon={<ArrowForwardIcon />}
                  onClick={() => navigate(`/student/courses/${course.courseId}`)}
                  sx={{ alignSelf: { xs: 'stretch', sm: 'center' } }}
                >
                  {course.completedAt ? 'Review Course' : 'Access Course'}
                </Button>
              </Stack>
              <LinearProgress
                variant="determinate"
                value={course.progressPercent}
                aria-label={`${course.title} completion`}
                aria-valuetext={`${course.progressPercent}% complete`}
              />
              <Stack direction={{ xs: 'column', sm: 'row' }} spacing={{ xs: 1, sm: 4 }}>
                <Box>
                  <Typography variant="caption" color="text.secondary">
                    Coursework time
                  </Typography>
                  <Typography fontWeight={600}>{timeLabel}</Typography>
                </Box>
                <Box>
                  <Typography variant="caption" color="text.secondary">
                    Quiz average
                  </Typography>
                  <Typography fontWeight={600}>{quizAverage}</Typography>
                </Box>
              </Stack>
            </Stack>
          </Paper>
        );
      })}
    </Stack>
  );
}
